import PropTypes from "prop-types";
import * as React from "react";
import { useProductTypesForUser } from "../../../hooks/useProductTypes";
import { bulkProductAction } from "../../../services/bulkProduct";
import { Alert, Button, Dialog, Select } from "../../../themeV2";
import css from "./BulkChangeProductTypeDialog.module.css";

const OTHER = "Other";

const BulkChangeProductTypeDialog = React.memo(
  function BulkChangeProductTypeDialog({
    open,
    onClose,
    selectedIds,
    currentProductTypeId,
    onActionComplete,
  }) {
    const [selectedTypeId, setSelectedTypeId] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const { productTypes } = useProductTypesForUser();

    const openRef = React.useRef(open);
    const [internalOpen, setInternalOpen] = React.useState(open);

    React.useEffect(() => {
      if (open !== openRef.current) {
        openRef.current = open;
        setInternalOpen(open);
      }
    }, [open]);

    const currentTypeName = React.useMemo(() => {
      if (currentProductTypeId === null) return OTHER;
      const found = productTypes.find(
        (pt) => pt.productTypeId === currentProductTypeId,
      );
      return found?.name ?? "Unknown";
    }, [currentProductTypeId, productTypes]);

    const availableTypes = React.useMemo(
      () => [
        ...productTypes.filter(
          (pt) => pt.productTypeId !== currentProductTypeId,
        ),
        ...(currentProductTypeId !== null
          ? [{ productTypeId: OTHER, name: OTHER }]
          : []),
      ],
      [productTypes, currentProductTypeId],
    );

    const handleClose = React.useCallback(() => {
      setSelectedTypeId("");
      setError(null);
      onClose();
    }, [onClose]);

    const handleChangeType = React.useCallback(async () => {
      if (selectedTypeId === "") return;

      setIsLoading(true);
      setError(null);

      const productTypeId =
        selectedTypeId === OTHER ? null : Number(selectedTypeId);

      try {
        await bulkProductAction({
          productIds: [...selectedIds],
          action: "changeProductType",
          productTypeId,
        });
        setSelectedTypeId("");
        onActionComplete();
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }, [selectedTypeId, selectedIds, onActionComplete]);

    const plural = selectedIds.size !== 1 ? "s" : "";

    return (
      <Dialog.Root
        open={internalOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isLoading) setInternalOpen(false);
        }}
        /* Resetting here rather than on close keeps the content still while the
           dialog animates out. */
        onOpenChangeComplete={(isOpen) => {
          if (!isOpen) handleClose();
        }}
      >
        <Dialog.Popup size="md">
          <Dialog.Title>
            Change Product Type for {selectedIds.size} Product{plural}
          </Dialog.Title>

          <Dialog.Description className={css.currentType}>
            Current type: <strong>{currentTypeName}</strong>
          </Dialog.Description>

          <Select.Root
            label="New Type"
            placeholder="Select a type"
            value={selectedTypeId}
            onValueChange={setSelectedTypeId}
          >
            {availableTypes.map((pt) => (
              <Select.Item
                key={pt.productTypeId}
                value={String(pt.productTypeId)}
              >
                {pt.name}
              </Select.Item>
            ))}
          </Select.Root>

          {error && (
            <div className={css.error}>
              <Alert severity="error">{error}</Alert>
            </div>
          )}

          <Dialog.Actions>
            <Button
              variant="text"
              disabled={isLoading}
              onClick={() => setInternalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              disabled={selectedTypeId === ""}
              loading={isLoading}
              onClick={handleChangeType}
            >
              Change Type
            </Button>
          </Dialog.Actions>
        </Dialog.Popup>
      </Dialog.Root>
    );
  },
);

BulkChangeProductTypeDialog.displayName = "BulkChangeProductTypeDialog";

BulkChangeProductTypeDialog.propTypes = {
  currentProductTypeId: PropTypes.number,
  onActionComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
};

export default BulkChangeProductTypeDialog;
